'use client'

import React, { useState } from 'react'
import { motion } from 'framer-motion'
import * as Icons from 'lucide-react'
import Link from 'next/link'
import servicesData from '@/data/services.json'
import { useScrollAnimation } from '@/hooks/useScrollAnimation'
import { WhatsAppIcon } from './WhatsAppIcon'
import { getWhatsAppUrl, PHONE_CALL_URL } from '@/lib/whatsapp'

type IconName = keyof typeof Icons

// Service name to URL mapping
const serviceUrlMap: Record<string, string> = {
  'Golden Visa Assistance': '/golden-visa',
  'Power of Attorney Services': '/power-of-attorney',
  'Business Power of Attorney': '/business-power-of-attorney',
  'True Copy Attestation': '/true-copy-attestation',
  'Notary Services': '/poa-services',
  'POA Notarization': '/poa-services',
  'Property Conveyancing': '/property-conveyancing',
  'Power of Attorney': '/power-of-attorney',
  'Document Attestation': '/document-services',
  'Car Power of Attorney': '/car-power-of-attorney',
  'Company Formation Support': '/company-formation',
  'Visa Processing Services': '/visa-processing',
  'Medical Coordination': '/medical-coordination',
  'Property Gifting': '/property-gifting',
}

const iconMap: Record<string, IconName> = {
  Building2: 'Building2',
  Passport: 'FileText',
  FileText: 'FileText',
  CheckCircle2: 'CheckCircle2',
  Stamp: 'Stamp',
  Copy: 'Copy',
  PenTool: 'PenTool',
  Shield: 'Shield',
  Home: 'Home',
  Gift: 'Gift',
  Stethoscope: 'Stethoscope',
  Zap: 'Zap',
  Users: 'Users',
  Building: 'Building2',
  Eye: 'Eye',
  ArrowRight: 'ArrowRight',
}

const categoryColors: Record<string, { bg: string; icon: string; badge: string }> = {
  'Business & Corporate': {
    bg: 'bg-blue-50',
    icon: 'text-[#1D4ED8] bg-[#DBEAFE]',
    badge: 'bg-[#DBEAFE] text-[#1D4ED8]',
  },
  'Legal Documentation': {
    bg: 'bg-amber-50',
    icon: 'text-[#D97706] bg-[#FEF3C7]',
    badge: 'bg-[#FEF3C7] text-[#D97706]',
  },
  'Property Services': {
    bg: 'bg-emerald-50',
    icon: 'text-[#059669] bg-[#D1FAE5]',
    badge: 'bg-[#D1FAE5] text-[#059669]',
  },
  'Medical & Government': {
    bg: 'bg-violet-50',
    icon: 'text-[#7C3AED] bg-[#EDE9FE]',
    badge: 'bg-[#EDE9FE] text-[#7C3AED]',
  },
}

const whyIconMap: Record<string, { icon: IconName; color: string; bg: string }> = {
  Zap: { icon: 'Zap', color: '#D97706', bg: '#FEF3C7' },
  Users: { icon: 'Users', color: '#1D4ED8', bg: '#DBEAFE' },
  CheckCircle2: { icon: 'CheckCircle2', color: '#059669', bg: '#D1FAE5' },
  Building: { icon: 'Building2', color: '#7C3AED', bg: '#EDE9FE' },
  Eye: { icon: 'Eye', color: '#0891B2', bg: '#CFFAFE' },
  ArrowRight: { icon: 'ArrowRight', color: '#E11D48', bg: '#FFE4E6' },
}

export function ServicesSection() {
  const { ref, isVisible } = useScrollAnimation()
  const categories = ['All', ...Array.from(new Set(servicesData.services.map((s) => s.category)))]
  const [activeCategory, setActiveCategory] = useState('All')

  const filteredServices =
    activeCategory === 'All'
      ? servicesData.services
      : servicesData.services.filter((s) => s.category === activeCategory)

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: 0.1 },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  }

  return (
    <section id="services" ref={ref} className="py-16 md:py-24 bg-[#F8FBFF]">
      <div className="container-narrow">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="section-badge">
            <Icons.Briefcase className="w-4 h-4" />
            Our Services
          </span>
          <h2 className="section-title">Comprehensive UAE Government & Clearing Services</h2>
          <p className="section-subtitle max-w-2xl mx-auto">
            Authorized document clearing and government relations services. Click below to chat directly with our specialist on WhatsApp for instant pricing.
          </p>
        </motion.div>

        {/* Category Filter Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="flex flex-wrap gap-2 justify-center mb-10"
        >
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-300 ${
                activeCategory === cat
                  ? 'bg-[#1D4ED8] text-white shadow-soft'
                  : 'bg-white text-[#475569] border border-[#DBEAFE] hover:border-[#1D4ED8] hover:text-[#1D4ED8]'
              }`}
            >
              {cat}
            </button>
          ))}
        </motion.div>

        {/* Services Grid */}
        <motion.div
          key={activeCategory}
          variants={containerVariants}
          initial="hidden"
          animate={isVisible ? 'visible' : 'hidden'}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20"
        >
          {filteredServices.map((service) => {
            const IconComponent = Icons[iconMap[service.icon] as IconName] as React.ComponentType<{ className?: string }>
            const colors = categoryColors[service.category] || categoryColors['Business & Corporate']
            const serviceWhatsAppUrl = getWhatsAppUrl({
              type: 'service',
              service: service.name,
            })

            return (
              <motion.div
                key={service.id}
                variants={itemVariants}
                className="bg-white rounded-2xl border border-[#DBEAFE] p-6 hover:-translate-y-1 group transition-all duration-300 flex flex-col justify-between"
                style={{ boxShadow: '0 4px 6px rgba(29,78,216,0.07)' }}
                whileHover={{ boxShadow: '0 10px 30px rgba(29,78,216,0.14)' }}
              >
                <div>
                  {/* Category + Popular Badge */}
                  <div className="flex items-start justify-between mb-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${colors.badge}`}>
                      {service.category}
                    </span>
                    {service.featured && (
                      <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-[#FEF3C7] text-[#D97706]">
                        Popular
                      </span>
                    )}
                  </div>

                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4 transition-all duration-300 ${colors.icon}`}>
                    {IconComponent && <IconComponent className="w-6 h-6" />}
                  </div>

                  <h3 className="text-lg font-bold text-[#1E293B] mb-2 group-hover:text-[#1D4ED8] transition-colors duration-300">
                    {service.name}
                  </h3>

                  <p className="text-[#64748B] text-sm leading-relaxed mb-3">
                    {service.description}
                  </p>

                  <p className="text-xs text-[#94A3B8] leading-relaxed mb-6">{service.details}</p>
                </div>

                {/* Conversion Action Row: 1-Tap WhatsApp Inquire + Learn More */}
                <div className="pt-4 border-t border-[#F1F5F9] flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5">
                  <a
                    id={`service-card-wa-${service.id}`}
                    href={serviceWhatsAppUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-xl text-white font-bold text-xs shadow-sm transition-all duration-200 hover:brightness-105 active:scale-95"
                    style={{ backgroundColor: '#25D366' }}
                    aria-label={`Inquire about ${service.name} on WhatsApp`}
                  >
                    <WhatsAppIcon className="w-3.5 h-3.5 fill-white" />
                    <span>Inquire on WhatsApp</span>
                  </a>

                  <Link
                    id={`service-card-link-${service.id}`}
                    href={serviceUrlMap[service.name] || '/contact'}
                    className="inline-flex items-center justify-center sm:justify-start gap-1 text-[#1D4ED8] font-semibold text-xs hover:text-[#1E40AF] transition-colors py-1"
                  >
                    <span>{serviceUrlMap[service.name] ? 'Learn More' : 'Details'}</span>
                    <Icons.ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </motion.div>
            )
          })}
        </motion.div>

        {/* Why Choose Us */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center mb-12"
        >
          <span className="section-badge-accent">
            <Icons.ThumbsUp className="w-4 h-4" />
            Why Choose Us
          </span>
          <h2 className="section-title">Why Thousands Trust Al Ishrat</h2>
          <p className="section-subtitle max-w-2xl mx-auto">
            Direct government relations access, 10+ years experience, and prompt communication every step of the way.
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isVisible ? 'visible' : 'hidden'}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {servicesData.whyChooseUs.map((item) => {
            const iconConfig = whyIconMap[item.icon] || { icon: 'CheckCircle2' as IconName, color: '#1D4ED8', bg: '#DBEAFE' }
            const IconComp = Icons[iconConfig.icon] as React.ComponentType<{ className?: string }>
            return (
              <motion.div
                key={item.id}
                variants={itemVariants}
                className="bg-white rounded-2xl p-6 border border-[#DBEAFE] hover:-translate-y-1 transition-all duration-300 group"
                style={{ boxShadow: '0 4px 6px rgba(29,78,216,0.07)' }}
              >
                <div
                  className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4 transition-all duration-300 group-hover:scale-110"
                  style={{ background: iconConfig.bg, color: iconConfig.color }}
                >
                  {IconComp && <IconComp className="w-6 h-6" />}
                </div>
                <h3 className="text-lg font-bold text-[#1E293B] mb-2 group-hover:text-[#1D4ED8] transition-colors duration-300">
                  {item.title}
                </h3>
                <p className="text-[#64748B] text-sm leading-relaxed">{item.description}</p>
              </motion.div>
            )
          })}
        </motion.div>

        {/* Bottom Conversion CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-16 rounded-3xl p-8 md:p-12 text-center text-white"
          style={{ background: 'linear-gradient(135deg, #1E3A8A 0%, #1D4ED8 100%)' }}
        >
          <h3 className="text-2xl md:text-3xl font-bold mb-3">
            Not Sure Which Service You Need?
          </h3>
          <p className="text-blue-100 mb-8 text-base md:text-lg max-w-xl mx-auto">
            Our government document specialists will assess your requirements and recommend the exact solution within minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-3.5 justify-center items-center">
            {/* Primary WhatsApp CTA in section banner */}
            <a
              id="services-bottom-whatsapp-btn"
              href={getWhatsAppUrl({ type: 'consultation' })}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl text-white font-extrabold text-base shadow-xl transition-all duration-300 hover:brightness-105 active:scale-98"
              style={{ backgroundColor: '#25D366' }}
            >
              <WhatsAppIcon className="w-5 h-5 fill-white" />
              <span>Chat on WhatsApp Now</span>
            </a>

            <a
              id="services-bottom-call-btn"
              href={PHONE_CALL_URL}
              className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-white/15 text-white rounded-xl font-bold text-sm border border-white/25 hover:bg-white/25 transition-all duration-300"
            >
              <Icons.Phone className="w-4 h-4 text-[#FCD34D]" />
              <span>Call +971 58 615 5899</span>
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
